import { Button, Toggle, useToast } from "@design";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { errorMessage, getCurrentUserInfoQueryKey, updateUserSettingsMutation } from "@/api";
import { PageSection } from "@/components/section/SectionBlocks";
import { useCapabilities, useCurrentUser } from "@/features/account/useCurrentUser";

/**
 * "Learn from my chats" (MHS-755): whether Anthm proposes knowledge-graph facts
 * from this user's chats into their Knowledge inbox. On unless they turn it
 * off; only shown where the deployment does extraction. Saves on change.
 */
export function LearningSection() {
  const { knowledgeExtractionEnabled } = useCapabilities();
  const me = useCurrentUser();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast();
  const { t } = useTranslation("settings");
  const save = useMutation({
    ...updateUserSettingsMutation(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: getCurrentUserInfoQueryKey() }),
    onError: (error) => toast.error(errorMessage(error, t("learning.saveFailed"))),
  });
  if (!knowledgeExtractionEnabled || !me.data) return null;

  // Stored snake_case in user.settings; null/absent means on.
  const stored = me.data.user.settings?.kg_learn_from_chats;
  const on = save.isPending ? Boolean(save.variables?.body.kgLearnFromChats) : stored !== false;

  return (
    <PageSection
      title={t("learning.title")}
      description={t("learning.description")}
      actions={
        <Toggle
          aria-label={t("learning.label")}
          checked={on}
          onChange={(checked) => save.mutate({ body: { kgLearnFromChats: checked } })}
        />
      }
    >
      <div>
        <Button variant="secondary" size="sm" onClick={() => navigate("/knowledge/inbox")}>
          {t("learning.review")}
        </Button>
      </div>
    </PageSection>
  );
}
